// pages/AnalysePageV2.jsx
// Page d'analyse decomposee : Correction → Traduction → Analyse

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Loader2, AlertCircle, CheckCircle, ArrowRight, Lock, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import './AnalysePage.css';
import './AnalysePageV2.css';

export default function AnalysePageV2() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';

  // Etat saisie
  const [sourceText, setSourceText] = useState('');
  const [sourceLang, setSourceLang] = useState('fr');
  const [targetLang, setTargetLang] = useState('es');

  // Options (checkboxes)
  const [wantCorrection, setWantCorrection] = useState(true);
  const [wantTraduction, setWantTraduction] = useState(false);
  const [wantAnalyseSource, setWantAnalyseSource] = useState(false);
  const [wantAnalyseTraduction, setWantAnalyseTraduction] = useState(false);

  // Limites abonnement
  const [limites, setLimites] = useState(null);
  const [anonUsage, setAnonUsage] = useState(null); // { current, limite, remaining }

  // Etapes
  const [step, setStep] = useState('saisie'); // saisie, resultats
  const [loading, setLoading] = useState(null); // null, 'correction', 'traduction', 'analyse-source', 'analyse-traduction'
  const [error, setError] = useState(null);
  const [mustRegister, setMustRegister] = useState(false);

  // Resultats
  const [correctionResult, setCorrectionResult] = useState(null);
  const [traductionResult, setTraductionResult] = useState(null);
  const [analyseSourceResult, setAnalyseSourceResult] = useState(null);
  const [analyseTraductionResult, setAnalyseTraductionResult] = useState(null);

  const languages = [
    { code: 'fr', name: t('langues.fr'), flag: '🇫🇷' },
    { code: 'en', name: t('langues.en'), flag: '🇬🇧' },
    { code: 'es', name: t('langues.es'), flag: '🇪🇸' },
  ];

  // Charger les limites (fonctionne connecte ou non)
  const fetchLimites = async () => {
    try {
      const response = await fetch('/api/analyse-limites');
      if (response.ok) {
        const data = await response.json();
        setLimites(data.limites);
        if (data.anonUsage) {
          setAnonUsage(data.anonUsage);
          if (data.anonUsage.remaining <= 0) {
            setMustRegister(true);
          }
        }
      }
    } catch (err) {
      console.error('Erreur chargement limites:', err);
    }
  };

  useEffect(() => {
    fetchLimites();
  }, []);

  const wordCount = sourceText.trim() ? sourceText.trim().split(/\s+/).filter(Boolean).length : 0;
  const maxWords = limites?.longueurTexteMax || 0;
  const isTextTooLong = maxWords !== -1 && wordCount > maxWords;

  const canCorrect = limites?.correction === true;
  const canTranslate = limites?.traduction === true;
  const canAnalyse = limites?.analyse === true;

  // Reset si on change la langue source = langue cible
  useEffect(() => {
    if (sourceLang === targetLang) {
      const other = languages.find((l) => l.code !== sourceLang);
      if (other) setTargetLang(other.code);
    }
  }, [sourceLang]);

  // Si traduction decochee, decocher analyse traduction
  useEffect(() => {
    if (!wantTraduction) {
      setWantAnalyseTraduction(false);
    }
  }, [wantTraduction]);

  // ========== LANCER TOUTES LES ACTIONS COCHEES ==========
  const handleLancer = async () => {
    if (!sourceText.trim()) return;

    setError(null);
    setCorrectionResult(null);
    setTraductionResult(null);
    setAnalyseSourceResult(null);
    setAnalyseTraductionResult(null);

    let textToProcess = sourceText;
    let translatedText = null;

    // 1. Correction (si cochee)
    if (wantCorrection) {
      setLoading('correction');
      try {
        const response = await fetch('/api/correct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: sourceText, langue: sourceLang }),
        });
        const data = await response.json();
        if (!response.ok) {
          if (data.mustRegister) setMustRegister(true);
          throw new Error(t('analyse.erreurCorrection'));
        }
        setCorrectionResult(data.data);
        // Utiliser le texte corrige pour les etapes suivantes
        if (data.data.correctedText) {
          textToProcess = data.data.correctedText;
        }
        // Rafraichir les limites anonymes
        if (!isAuthenticated) fetchLimites();
      } catch (err) {
        setError(err.message);
        setLoading(null);
        return;
      }
    }

    // 2. Traduction (si cochee)
    if (wantTraduction) {
      setLoading('traduction');
      try {
        const response = await fetch('/api/translate-only', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: textToProcess, sourceLang, targetLang }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(t('analyse.erreurTraduction'));
        setTraductionResult(data.data);
        translatedText = data.data.translation;
      } catch (err) {
        setError(err.message);
        setLoading(null);
        return;
      }
    }

    // 3. Analyse source (si cochee)
    if (wantAnalyseSource) {
      setLoading('analyse-source');
      try {
        const response = await fetch('/api/analyze-only', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: textToProcess, langue: sourceLang }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(t('analyse.erreurAnalyseSource'));
        setAnalyseSourceResult(data.data);
      } catch (err) {
        setError(err.message);
        setLoading(null);
        return;
      }
    }

    // 4. Analyse traduction (si cochee et traduction disponible)
    if (wantAnalyseTraduction && translatedText) {
      setLoading('analyse-traduction');
      try {
        const response = await fetch('/api/analyze-only', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: translatedText, langue: targetLang }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(t('analyse.erreurAnalyseTraduction'));
        setAnalyseTraductionResult(data.data);
      } catch (err) {
        setError(err.message);
        setLoading(null);
        return;
      }
    }

    setLoading(null);
    setStep('resultats');
  };

  // Recommencer
  const handleReset = () => {
    setStep('saisie');
    setCorrectionResult(null);
    setTraductionResult(null);
    setAnalyseSourceResult(null);
    setAnalyseTraductionResult(null);
    setError(null);
  };

  const hasAnyOption = wantCorrection || wantTraduction || wantAnalyseSource || wantAnalyseTraduction;

  // ========== COMPOSANT : Affichage diff corrections ==========
  const CorrectionDiff = ({ corrections, correctedText, originalText }) => {
    if (!corrections || corrections.length === 0) {
      return (
        <div className="correction-ok">
          <CheckCircle size={20} color="#16a34a" />
          <span>{t('analyse.aucuneCorrection')}</span>
        </div>
      );
    }

    return (
      <div className="correction-diff">
        <div className="corrected-text-box">
          <h4>{t('analyse.texteCorrige')}</h4>
          <p className="corrected-text">{correctedText}</p>
        </div>
        <div className="corrections-list">
          <h4>{t('analyse.correctionsCount', { count: corrections.length })}</h4>
          {corrections.map((c, i) => (
            <div key={i} className="correction-item">
              <div className="correction-words">
                <span className="word-removed">{c.original}</span>
                <ArrowRight size={14} color="#9CA3AF" />
                <span className="word-added">{c.corrected}</span>
                <span className="correction-type-badge">{c.type}</span>
              </div>
              <p className="correction-explanation">{c.explanation}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========== COMPOSANT : Section d'analyse grammaticale ==========
  const AnalysisSection = ({ data, title, color }) => {
    if (!data) return null;

    return (
      <div className="analysis-section">
        <div className="analysis-box">
          <h3 className="analysis-title">
            <div className="title-bar" style={{ backgroundColor: color }}></div>
            {title}
          </h3>

          {/* Verbes */}
          {data.verbs && data.verbs.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.verbes')}</h4>
              <div className="verb-list">
                {data.verbs.map((v, i) => (
                  <div key={i} className="verb-card">
                    <div className="verb-header">
                      <span className="verb-badge original">{v.original}</span>
                      <span className="verb-badge-info">→ {v.infinitive}</span>
                    </div>
                    <div className="verb-details">
                      <div><span className="verb-detail-label">{t('analyse.tempsLabel')}</span><span className="verb-detail-value">{v.tense}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.modeLabel')}</span><span className="verb-detail-value">{v.mood}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.personneLabel')}</span><span className="verb-detail-value">{v.person}</span></div>
                    </div>
                    {v.explanation && <p className="verb-explanation">💡 {v.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Noms */}
          {data.nouns && data.nouns.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.noms')}</h4>
              <div className="verb-list">
                {data.nouns.map((n, i) => (
                  <div key={i} className="verb-card">
                    <div className="verb-header">
                      <span className="verb-badge original">{n.original}</span>
                    </div>
                    <div className="verb-details">
                      <div><span className="verb-detail-label">{t('analyse.genreLabel')}</span><span className="verb-detail-value">{n.gender}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.nombreLabel')}</span><span className="verb-detail-value">{n.number}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.typeLabel')}</span><span className="verb-detail-value">{n.type}</span></div>
                    </div>
                    {n.explanation && <p className="verb-explanation">💡 {n.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adjectifs */}
          {data.adjectives && data.adjectives.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.adjectifs')}</h4>
              <div className="verb-list">
                {data.adjectives.map((a, i) => (
                  <div key={i} className="verb-card">
                    <div className="verb-header">
                      <span className="verb-badge original">{a.original}</span>
                    </div>
                    <div className="verb-details">
                      <div><span className="verb-detail-label">{t('analyse.genreLabel')}</span><span className="verb-detail-value">{a.gender}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.nombreLabel')}</span><span className="verb-detail-value">{a.number}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.positionLabel')}</span><span className="verb-detail-value">{a.position}</span></div>
                    </div>
                    {a.explanation && <p className="verb-explanation">💡 {a.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pronoms */}
          {data.pronouns && data.pronouns.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.pronoms')}</h4>
              <div className="verb-list">
                {data.pronouns.map((p, i) => (
                  <div key={i} className="verb-card">
                    <div className="verb-header">
                      <span className="verb-badge original">{p.original}</span>
                    </div>
                    <div className="verb-details">
                      <div><span className="verb-detail-label">{t('analyse.typeLabel')}</span><span className="verb-detail-value">{p.type}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.personneLabel')}</span><span className="verb-detail-value">{p.person}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.fonctionLabel')}</span><span className="verb-detail-value">{p.function}</span></div>
                    </div>
                    {p.explanation && <p className="verb-explanation">💡 {p.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Articles */}
          {data.articles && data.articles.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.articles')}</h4>
              <div className="verb-list">
                {data.articles.map((a, i) => (
                  <div key={i} className="verb-card">
                    <div className="verb-header">
                      <span className="verb-badge original">{a.original}</span>
                    </div>
                    <div className="verb-details">
                      <div><span className="verb-detail-label">{t('analyse.typeLabel')}</span><span className="verb-detail-value">{a.type}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.genreLabel')}</span><span className="verb-detail-value">{a.gender}</span></div>
                      <div><span className="verb-detail-label">{t('analyse.nombreLabel')}</span><span className="verb-detail-value">{a.number}</span></div>
                    </div>
                    {a.explanation && <p className="verb-explanation">💡 {a.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prepositions */}
          {data.prepositions && data.prepositions.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.prepositions')}</h4>
              <div className="verb-list">
                {data.prepositions.map((p, i) => (
                  <div key={i} className="verb-card">
                    <div className="verb-header">
                      <span className="verb-badge original">{p.original}</span>
                    </div>
                    <div className="verb-details">
                      <div><span className="verb-detail-label">{t('analyse.usageLabel')}</span><span className="verb-detail-value">{p.usage}</span></div>
                    </div>
                    {p.explanation && <p className="verb-explanation">💡 {p.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adverbes */}
          {data.adverbs && data.adverbs.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.adverbes')}</h4>
              <div className="verb-list">
                {data.adverbs.map((a, i) => (
                  <div key={i} className="verb-card">
                    <div className="verb-header">
                      <span className="verb-badge original">{a.original}</span>
                    </div>
                    <div className="verb-details">
                      <div><span className="verb-detail-label">{t('analyse.typeLabel')}</span><span className="verb-detail-value">{a.type}</span></div>
                    </div>
                    {a.explanation && <p className="verb-explanation">💡 {a.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Points grammaticaux */}
          {data.grammar && data.grammar.length > 0 && (
            <div className="analysis-category">
              <h4 className="category-title">{t('analyse.pointsGrammaticaux')}</h4>
              <div className="grammar-list">
                {data.grammar.map((g, i) => (
                  <div key={i} className="grammar-card">
                    <div className="grammar-content">
                      <span className="grammar-type">{g.type}</span>
                      <div>
                        <p className="grammar-text">{g.text}</p>
                        <p className="grammar-explanation">{g.explanation}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== RENDU ==========
  return (
    <div>
      {/* Erreur */}
      {error && (
        <div className="error-box">
          <AlertCircle size={20} color="#DC2626" />
          <div>
            <p className="error-title">{t('analyse.erreurTitre')}</p>
            <p className="error-message">{error}</p>
          </div>
        </div>
      )}

      {!isEmbed && (
        <div className="analyse-header">
          <Globe size={32} color="#4F46E5" />
          <h2>{t('analyse.headerTitre')}</h2>
        </div>
      )}

      {/* Bandeau anonyme */}
      {!isAuthenticated && !mustRegister && anonUsage && (
        <div className="anon-banner">
          <span>{t('analyse.anonModeDecouverte', { count: anonUsage.remaining, limite: anonUsage.limite })}</span>
          <Link to="/register" className="anon-register-link">
            <UserPlus size={16} /> {t('analyse.creerCompteGratuit')}
          </Link>
        </div>
      )}

      {/* Blocage anonyme : doit s'inscrire */}
      {mustRegister && (
        <div className="must-register-box">
          <h3>{t('analyse.limiteAtteinteTitre')}</h3>
          <p>{t('analyse.vousAvezUtilise', { limite: anonUsage?.limite || 5 })}</p>
          <p>{t('analyse.creezCompteGratuitContinuer')}</p>
          <div className="must-register-buttons">
            <Link to="/register" className="btn-translate">
              <UserPlus size={18} /> {t('auth.creerUnCompte')}
            </Link>
            <Link to="/login" className="btn-secondary">
              {t('auth.seConnecter')}
            </Link>
          </div>
        </div>
      )}

      {/* ========== ZONE DE SAISIE ========== */}
      {!mustRegister && <div className="translation-box">
        {/* Selecteur langue du texte */}
        <div className="language-selectors">
          <div className="lang-selector">
            <label className="lang-label">{t('analyse.langueDuTexte')}</label>
            <select
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
              className="lang-select"
              disabled={step === 'resultats'}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* Selecteur langue cible - visible si traduction cochee */}
          {wantTraduction && (
            <div className="lang-selector">
              <label className="lang-label">{t('analyse.traduireVers')}</label>
              <select
                value={targetLang}
                onChange={(e) => setTargetLang(e.target.value)}
                className="lang-select"
                disabled={step === 'resultats'}
              >
                {languages
                  .filter((l) => l.code !== sourceLang)
                  .map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>

        {/* Zone de texte */}
        <div className="text-section" style={{ marginTop: 0 }}>
          <textarea
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            placeholder={t('analyse.placeholderTexte')}
            className="text-input"
            disabled={step === 'resultats'}
          />
          <div className="word-count-bar">
            <span className={`word-count ${isTextTooLong ? 'over-limit' : ''}`}>
              {t('analyse.motCount', { count: wordCount })}
              {maxWords > 0 && maxWords !== -1 && t('analyse.motMax', { max: maxWords })}
            </span>
          </div>
        </div>

        {/* ========== CHECKBOXES OPTIONS ========== */}
        <div className="options-section">
          <div className={`option-checkbox ${!canCorrect ? 'disabled' : ''}`}>
            <label>
              <input
                type="checkbox"
                checked={wantCorrection}
                onChange={(e) => setWantCorrection(e.target.checked)}
                disabled={!canCorrect || step === 'resultats'}
              />
              <span>{t('analyse.corriger')}</span>
              {!canCorrect && <Lock size={14} className="lock-icon" />}
            </label>
          </div>
          <div className={`option-checkbox ${!canTranslate ? 'disabled' : ''}`}>
            <label>
              <input
                type="checkbox"
                checked={wantTraduction}
                onChange={(e) => setWantTraduction(e.target.checked)}
                disabled={!canTranslate || step === 'resultats'}
              />
              <span>{t('analyse.traduire')}</span>
              {!canTranslate && <Lock size={14} className="lock-icon" />}
            </label>
          </div>
          <div className={`option-checkbox ${!canAnalyse ? 'disabled' : ''}`}>
            <label>
              <input
                type="checkbox"
                checked={wantAnalyseSource}
                onChange={(e) => setWantAnalyseSource(e.target.checked)}
                disabled={!canAnalyse || step === 'resultats'}
              />
              <span>{t('analyse.analyserTexteSource')}</span>
              {!canAnalyse && <Lock size={14} className="lock-icon" />}
            </label>
          </div>
          <div className={`option-checkbox ${!canAnalyse || !wantTraduction ? 'disabled' : ''}`}>
            <label>
              <input
                type="checkbox"
                checked={wantAnalyseTraduction}
                onChange={(e) => setWantAnalyseTraduction(e.target.checked)}
                disabled={!canAnalyse || !wantTraduction || step === 'resultats'}
              />
              <span>{t('analyse.analyserTraduction')}</span>
              {!canAnalyse && <Lock size={14} className="lock-icon" />}
              {canAnalyse && !wantTraduction && <span className="option-hint">{t('analyse.cocherTraduireDabord')}</span>}
            </label>
          </div>
        </div>

        {/* ========== BOUTONS D'ACTION ========== */}
        <div className="btn-center">
          {step === 'saisie' && (
            <button
              type="button"
              onClick={handleLancer}
              disabled={!sourceText.trim() || loading || isTextTooLong || !hasAnyOption}
              className="btn-translate"
            >
              {loading ? (
                <><Loader2 size={20} className="spinning" /> {
                  loading === 'correction' ? t('analyse.correctionEnCours') :
                  loading === 'traduction' ? t('analyse.traductionEnCours') :
                  loading === 'analyse-source' ? t('analyse.analyseTexteEnCours') :
                  loading === 'analyse-traduction' ? t('analyse.analyseTraductionEnCours') :
                  t('analyse.enCours')
                }</>
              ) : (
                <>{t('analyse.lancer')}</>
              )}
            </button>
          )}

          {step === 'resultats' && (
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              {t('analyse.nouveauTexte')}
            </button>
          )}
        </div>
      </div>}

      {/* ========== RESULTATS : CORRECTION ========== */}
      {correctionResult && (
        <div className="result-section">
          <div className="result-box">
            <h3 className="result-title">
              <div className="title-bar" style={{ backgroundColor: '#F59E0B' }}></div>
              {t('analyse.correctionTitre')}
            </h3>
            <CorrectionDiff
              corrections={correctionResult.corrections}
              correctedText={correctionResult.correctedText}
              originalText={sourceText}
            />
          </div>
        </div>
      )}

      {/* ========== RESULTATS : TRADUCTION ========== */}
      {traductionResult && (
        <div className="result-section">
          <div className="result-box">
            <h3 className="result-title">
              <div className="title-bar" style={{ backgroundColor: '#4F46E5' }}></div>
              {t('analyse.traductionTitre', { flag: languages.find((l) => l.code === targetLang)?.flag, name: languages.find((l) => l.code === targetLang)?.name })}
            </h3>
            <div className="translated-text-box">
              <p>{traductionResult.translation}</p>
            </div>
            {traductionResult.notes && traductionResult.notes.length > 0 && (
              <div className="translation-notes">
                <h4>{t('analyse.notesTitre')}</h4>
                {traductionResult.notes.map((n, i) => (
                  <div key={i} className="note-item">
                    <span className="note-original">{n.original}</span>
                    <span className="note-text">{n.note}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== RESULTATS : ANALYSE SOURCE ========== */}
      {analyseSourceResult && (
        <AnalysisSection
          data={analyseSourceResult}
          title={t('analyse.analyseGrammaticaleTitre', { flag: languages.find((l) => l.code === sourceLang)?.flag, label: t('analyse.texteSource') })}
          color="#16a34a"
        />
      )}

      {/* ========== RESULTATS : ANALYSE TRADUCTION ========== */}
      {analyseTraductionResult && (
        <AnalysisSection
          data={analyseTraductionResult}
          title={t('analyse.analyseGrammaticaleTitre', { flag: languages.find((l) => l.code === targetLang)?.flag, label: t('analyse.traductionLabel') })}
          color="#8B5CF6"
        />
      )}

      {/* ========== OVERLAY CHARGEMENT ========== */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <div className="loading-popup-icon">
              <Loader2 size={48} color="#4F46E5" className="spinning" style={{ margin: '0 auto' }} />
            </div>
            <h3 className="loading-popup-title">
              {loading === 'correction' && t('analyse.loadingPopupCorrection')}
              {loading === 'traduction' && t('analyse.loadingPopupTraduction')}
              {loading === 'analyse-source' && t('analyse.loadingPopupAnalyseSource')}
              {loading === 'analyse-traduction' && t('analyse.loadingPopupAnalyseTraduction')}
            </h3>
            <p className="loading-popup-message">
              {t('analyse.traitementIA')}<br />{t('analyse.celaPeutPrendre')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
